import { useParams, Link } from 'react-router-dom';
import { getArticleBySlug, getRelatedArticles } from '@/mocks/articles';
import { siteInfo } from '@/mocks/site';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <h2 className="font-heading text-2xl text-foreground-900">文章未找到</h2>
          <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 text-sm">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = getRelatedArticles(article, 3);

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: siteInfo.url },
          { '@type': 'ListItem', position: 2, name: article.categoryName, item: `${siteInfo.url}/category/${article.category}` },
          { '@type': 'ListItem', position: 3, name: article.title, item: `${siteInfo.url}/article/${article.slug}` },
        ],
      },
      {
        '@type': 'Article',
        '@id': `${siteInfo.url}/article/${article.slug}`,
        headline: article.title,
        description: article.excerpt,
        author: { '@type': 'Person', name: article.author },
        datePublished: article.publishDate,
        image: article.imageUrl,
        url: `${siteInfo.url}/article/${article.slug}`,
        publisher: {
          '@type': 'Organization',
          name: siteInfo.name,
          url: siteInfo.url,
        },
        articleSection: article.categoryName,
        wordCount: article.content.length,
      },
      {
        '@type': 'WebPage',
        '@id': `${siteInfo.url}/article/${article.slug}#webpage`,
        url: `${siteInfo.url}/article/${article.slug}`,
        name: `${article.title} - ${siteInfo.name}`,
        isPartOf: { '@id': `${siteInfo.url}/#website` },
      },
    ],
  };

  return (
    <>
      <title>{article.title} - 萝幼社</title>
      <meta name="description" content={article.excerpt} />
      <meta name="keywords" content={article.tags.join(',')} />
      <meta name="author" content={article.author} />
      <link rel="canonical" href={`${siteInfo.url}/article/${article.slug}`} />
      <meta property="og:title" content={article.title} />
      <meta property="og:description" content={article.excerpt} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={`${siteInfo.url}/article/${article.slug}`} />
      <meta property="og:image" content={article.imageUrl} />
      <meta property="article:published_time" content={article.publishDate} />
      <meta property="article:author" content={article.author} />
      <meta property="article:section" content={article.categoryName} />
      {article.tags.map((tag) => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={article.title} />
      <meta name="twitter:description" content={article.excerpt} />
      <meta name="twitter:image" content={article.imageUrl} />
      <script type="application/ld+json">
        {JSON.stringify(schemaData)}
      </script>

      <main>
        <div className="pt-16">
          <Breadcrumb />

          <article className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-50">
            <div className="max-w-4xl mx-auto">
              <div className="mb-2">
                <Link
                  to={`/category/${article.category}`}
                  className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700 hover:bg-accent-200 transition-colors cursor-pointer"
                >
                  {article.categoryName}
                </Link>
              </div>

              <h1 className="font-heading text-2xl md:text-4xl text-foreground-900 leading-tight mt-4">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 mt-6 text-sm text-foreground-500">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                    <img
                      src={article.authorAvatar}
                      alt={article.author}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span className="font-medium text-foreground-800">{article.author}</span>
                </div>
                <span>·</span>
                <span>{article.publishDate}</span>
                <span>·</span>
                <span>{article.readTime}</span>
                <span>·</span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.viewCount.toLocaleString()} 阅读
                </span>
              </div>

              <div className="mt-8 rounded-lg overflow-hidden aspect-[16/9]">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div
                className="mt-8 prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
                  [&_h2]:font-heading [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:text-foreground-900 [&_h2]:mt-10 [&_h2]:mb-4
                  [&_h3]:font-heading [&_h3]:text-lg [&_h3]:text-foreground-800 [&_h3]:mt-8 [&_h3]:mb-3
                  [&_p]:text-foreground-700 [&_p]:leading-relaxed [&_p]:mb-4
                  [&_strong]:text-foreground-900"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="flex flex-wrap items-center gap-2 mt-10 pt-6 border-t border-background-200">
                <span className="text-xs text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="flex items-center gap-6 mt-8 pt-6 border-t border-background-200">
                <button className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer">
                  <i className="ri-heart-line text-lg"></i>
                  <span>{article.likeCount.toLocaleString()} 赞</span>
                </button>
                <button className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer">
                  <i className="ri-chat-1-line text-lg"></i>
                  <span>{article.commentCount.toLocaleString()} 评论</span>
                </button>
                <button className="flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer">
                  <i className="ri-share-forward-line text-lg"></i>
                  <span>分享</span>
                </button>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
              <div className="max-w-7xl mx-auto">
                <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-8">
                  相关推荐
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.slug}`}
                      className="group rounded-lg overflow-hidden bg-background-50 hover:bg-background-200/50 transition-all duration-300 cursor-pointer"
                    >
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src={rel.imageUrl}
                          alt={rel.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="font-heading text-sm text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2">
                          {rel.title}
                        </h3>
                        <p className="mt-1 text-xs text-foreground-500">{rel.publishDate}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </div>
      </main>
    </>
  );
}